import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { map, catchError, timeout } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ReceivedOrder } from '../../../../domain/model/ingested-order/ingested-order.model';
import {
  IngestedOrdersQueryParams,
  OrderStatus
} from '@infra-services/ingested-orders/api/ingested-orders-api.interface';
import { OrdersService } from '../../orders/orders.service';
import {
  FilterOptionsResponse,
  ReprocessCause
} from '../../../../domain/model/filter/filter-options.model';
import { IngestedOrdersService } from '../../ingested-orders/services/ingested-orders.service';
import { AppHttpClientApi } from '../../../helpers/api/http-client-api';
import { HttpErrorHandlerService } from '../../../../core/error-handling/http-error-handler.service';

export interface FinalSaveRequest {
  caseNumbers: string[];
}

export interface FinalSaveResponse {
  specimenCount: number;
  message: string;
  timestamp: string;
  username: string;
}

/**
 * Service for managing received orders (status=RECEIVED)
 * Wrapper around OrdersService with default status=RECEIVED
 */
@Injectable({
  providedIn: 'root'
})
export class ReceivedOrdersService {
  private static readonly DEFAULT_STATUS = OrderStatus.RECIBIDA;
  private readonly ordersService = inject(OrdersService);
  private readonly ingestedOrdersService = inject(IngestedOrdersService);
  private readonly httpClient = inject(AppHttpClientApi);
  private readonly errorHandler = inject(HttpErrorHandlerService);

  /**
   * Get paginated list of received orders (status=RECEIVED)
   * Delegates to OrdersService with RECIBIDA status
   */
  getOrders(params?: IngestedOrdersQueryParams): Observable<{
    orders: ReceivedOrder[];
    pagination: {
      page: number;
      size: number;
      total: number;
      totalPages: number;
    };
  }> {
    return this.ordersService.getOrders({
      ...params,
      status: ReceivedOrdersService.DEFAULT_STATUS
    }) as Observable<{
      orders: ReceivedOrder[];
      pagination: {
        page: number;
        size: number;
        total: number;
        totalPages: number;
      };
    }>;
  }

  /**
   * Get filter options including reprocess causes
   * Extends base filter options with reprocess-specific data
   */
  getFilterOptions(): Observable<FilterOptionsResponse> {
    return forkJoin({
      baseOptions: this.ingestedOrdersService.getFilterOptions(),
      reprocessCauses: this.getReprocessCauses().pipe(catchError(() => of([])))
    }).pipe(
      map(({ baseOptions, reprocessCauses }) => ({
        ...baseOptions,
        reprocessCauses
      }))
    );
  }

  finalSave(caseNumbers: string[]): Observable<FinalSaveResponse> {
    const request: FinalSaveRequest = { caseNumbers };

    return this.httpClient
      .postRaw<FinalSaveResponse>('pre-admissions/final-save', request)
      .pipe(
        timeout(35000),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('finalSave', error)
        )
      );
  }

  private getReprocessCauses(): Observable<ReprocessCause[]> {
    return this.httpClient.getRaw<ReprocessCause[]>(
      'master-data/reprocess-causes'
    );
  }
}
